import {cn} from '@sollapay/ui';
import {ProgressBar, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {ACTIVATION_PACKAGE_CONFIG, getStepKeysForTab} from '../config';
import {getIncompleteSteps} from '../utils';

import type {ActivationPackageViewModel} from '../hooks';
import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {FC} from 'react';

export type ProgressPackage = Pick<ActivationPackageViewModel, 'type' | 'readiness'>;

export interface ActivationProgressBarProps {
  packages: ProgressPackage[];
  className?: string;
}

export const ActivationProgressBar: FC<ActivationProgressBarProps> = ({packages, className}) => {
  const {t} = useTranslation('trustAccounts');
  const strategy = useTrustTypeStrategy();
  const percent = computeCompletedStepsPercent(packages, strategy);
  const label = t('activation.progress.label');

  return (
    <div data-slot="activation-progress" className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center justify-between gap-4">
        <Typography size="sm" color="tertiary">
          {label}
        </Typography>
        <Typography size="sm" weight="bold" className="tabular-nums">
          {t('activation.progress.percent', {percent})}
        </Typography>
      </div>
      <ProgressBar value={percent} aria-label={label} />
    </div>
  );
};

/** Share of steps with nothing missing, across the required (non-optional) packages only. */
function computeCompletedStepsPercent(
  packages: ProgressPackage[],
  strategy: TrustTypeStrategy
): number {
  let total = 0;
  let completed = 0;

  for (const {type, readiness} of packages) {
    if (ACTIVATION_PACKAGE_CONFIG[type].isOptional) continue;
    const steps = getStepKeysForTab(type, strategy);
    total += steps.length;
    // Not ready with nothing listed missing is the no-data fallback, so no step counts as done.
    if (!readiness.isReady && readiness.missing.length === 0) continue;
    const incomplete = getIncompleteSteps(type, readiness.missing);
    completed += steps.filter(step => !incomplete.has(step)).length;
  }

  return total === 0 ? 0 : Math.round((completed / total) * 100);
}
